/**
 * RecordOfflineInvoiceDialog (Iter 37)
 *
 * Records an invoice that was raised OUTSIDE the system (Tally / SAP / manual
 * Excel) against an AMC schedule. Marks that billing cycle as billed so the
 * scheduler doesn't regenerate it. Optional PDF/PNG/JPG attachment (≤10 MB)
 * uploaded to object storage and permanently linked to the invoice.
 *
 * RBAC: super_admin + accounts (gated at the AMC page level).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowsClockwise, Receipt, Paperclip, Upload, X } from "@phosphor-icons/react";
import { api, formatApiError, API_BASE, getToken } from "../lib/api";
import { formatINR } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

const blank = (sched) => {
    const today = new Date().toISOString().slice(0, 10);
    return {
        external_invoice_number: "",
        invoice_date: today,
        period_start: sched?.next_due_date?.slice(0, 10) || today,
        period_end: "",
        amount: "",
        gst_amount: "",
        remarks: "",
        // Iter 41: client PO override — prefill from the AMC schedule's
        // default so the operator sees the inherited reference; can be
        // overridden per-invoice when the offline bill was raised under a
        // different PO.
        external_po_number: sched?.external_po_number || "",
        external_po_date: (sched?.external_po_date || "").slice(0, 10),
    };
};

export default function RecordOfflineInvoiceDialog({ open, onOpenChange, sched, onRecorded }) {
    const [form, setForm] = useState(blank(sched));
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [preview, setPreview] = useState(null);
    const baselineRef = useRef(JSON.stringify(blank(sched)));
    const dropRef = useRef(null);

    // Fetch the periodic amount to prefill (operator can override)
    useEffect(() => {
        if (!open || !sched?.id) return;
        (async () => {
            try {
                const { data } = await api.get(`/billing/amc/${sched.id}/preview-next-invoice`);
                setPreview(data);
            } catch (err) {
                // non-fatal — operator can still record without prefill
                setPreview(null);
            }
        })();
    }, [open, sched?.id]);

    // Reset baseline when dialog opens
    useEffect(() => {
        if (!open) return;
        const seed = blank(sched);
        if (preview?.preview) {
            seed.amount = String(preview.preview.subtotal || "");
            seed.gst_amount = String(preview.preview.tax_amount || "");
            seed.period_start = preview.preview.service_period_start || seed.period_start;
            seed.period_end = preview.preview.service_period_end || seed.period_end;
        }
        setForm(seed);
        setFile(null);
        baselineRef.current = JSON.stringify(seed);
    }, [open, sched, preview]);

    const isDirty = useMemo(
        () => JSON.stringify(form) !== baselineRef.current || file !== null,
        [form, file],
    );

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const handleFile = (f) => {
        if (!f) return setFile(null);
        if (!ALLOWED_MIME.includes(f.type)) {
            toast.error("Only PDF, PNG, or JPG files are allowed");
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error(`File exceeds 10 MB (got ${(f.size / 1024 / 1024).toFixed(1)} MB)`);
            return;
        }
        setFile(f);
    };

    const submit = async () => {
        if (!form.external_invoice_number.trim()) {
            toast.error("External invoice number is required");
            return;
        }
        if (!form.invoice_date || !form.period_start || !form.period_end) {
            toast.error("Invoice date, period start and period end are required");
            return;
        }
        if (form.period_end < form.period_start) {
            toast.error("Period end must be on or after period start");
            return;
        }
        const amt = Number(form.amount);
        if (!amt || amt < 0) {
            toast.error("Amount must be greater than 0");
            return;
        }

        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("external_invoice_number", form.external_invoice_number.trim());
            fd.append("invoice_date", form.invoice_date);
            fd.append("period_start", form.period_start);
            fd.append("period_end", form.period_end);
            fd.append("amount", String(amt));
            if (form.gst_amount) fd.append("gst_amount", String(Number(form.gst_amount)));
            if (form.remarks.trim()) fd.append("remarks", form.remarks.trim());
            // Iter 41: client PO override (optional). Empty → backend keeps
            // the schedule's default; non-empty → per-invoice override.
            if (form.external_po_number.trim()) fd.append("external_po_number", form.external_po_number.trim());
            if (form.external_po_date) fd.append("external_po_date", form.external_po_date);
            if (file) fd.append("attachment", file);

            // Multipart upload — use raw fetch to keep the boundary intact
            const resp = await fetch(
                `${API_BASE}/billing/amc/${sched.id}/record-offline-invoice`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${getToken()}` },
                    body: fd,
                },
            );
            if (!resp.ok) {
                let detail = "Failed to record offline invoice";
                try { detail = (await resp.json()).detail || detail; } catch { /* not JSON */ }
                throw new Error(detail);
            }
            const data = await resp.json();
            toast.success(`Recorded offline invoice ${data.external_invoice_number} · ${formatINR(data.total_amount)}`);
            baselineRef.current = JSON.stringify(form);
            onRecorded?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.message || "Failed to record offline invoice");
        } finally {
            setSubmitting(false);
        }
    };

    if (!sched) return null;
    const periodicHint = preview?.preview
        ? `Schedule's next period: ${preview.preview.service_period_start} → ${preview.preview.service_period_end} · ${formatINR(preview.preview.total_amount)}`
        : null;

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            contentClassName="max-w-2xl"
            testId="record-offline-dialog"
            title={
                <span className="flex items-center gap-2">
                    <Receipt size={18} weight="duotone" className="text-slate-700" />
                    Record offline invoice
                </span>
            }
            description={
                <>
                    For invoices raised outside the system (Tally / SAP / Excel). Marks the cycle
                    as billed so the scheduler won't regenerate it. <b>Once recorded, this entry
                    becomes part of the AMC's billing history.</b>
                </>
            }
        >
            <div className="space-y-4 mt-2" data-testid="record-offline-form">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">External invoice number *</Label>
                        <Input
                            value={form.external_invoice_number}
                            onChange={(e) => set("external_invoice_number", e.target.value)}
                            placeholder="e.g. TLY/2526/0042"
                            className="mt-1.5 font-mono"
                            data-testid="offline-external-number"
                        />
                        <div className="text-[10px] text-slate-500 mt-1">From your ERP / Tally / SAP — preserved verbatim.</div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Invoice date *</Label>
                        <Input
                            type="date"
                            value={form.invoice_date}
                            onChange={(e) => set("invoice_date", e.target.value)}
                            className="mt-1.5"
                            data-testid="offline-invoice-date"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Period start *</Label>
                        <Input
                            type="date"
                            value={form.period_start}
                            onChange={(e) => set("period_start", e.target.value)}
                            className="mt-1.5"
                            data-testid="offline-period-start"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Period end *</Label>
                        <Input
                            type="date"
                            value={form.period_end}
                            onChange={(e) => set("period_end", e.target.value)}
                            className="mt-1.5"
                            data-testid="offline-period-end"
                        />
                    </div>
                </div>
                {periodicHint && (
                    <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5">
                        <span className="font-semibold text-slate-700">Suggested:</span> {periodicHint}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Amount (pre-GST) *</Label>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={form.amount}
                            onChange={(e) => set("amount", e.target.value.replace(/[^\d.]/g, ""))}
                            placeholder="0"
                            className="mt-1.5 text-right font-mono"
                            data-testid="offline-amount"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">GST amount</Label>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={form.gst_amount}
                            onChange={(e) => set("gst_amount", e.target.value.replace(/[^\d.]/g, ""))}
                            placeholder="0"
                            className="mt-1.5 text-right font-mono"
                            data-testid="offline-gst"
                        />
                    </div>
                </div>

                <div>
                    <Label className="text-xs font-semibold">Remarks</Label>
                    <Textarea
                        value={form.remarks}
                        onChange={(e) => set("remarks", e.target.value)}
                        rows={2}
                        placeholder="Optional context — why offline, source system, ref to original PO, etc."
                        className="mt-1.5"
                        data-testid="offline-remarks"
                    />
                </div>

                {/* Iter 41: Client PO override — pre-filled from the AMC schedule */}
                <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3">
                    <div className="text-[11px] reedos-eyebrow text-slate-600 mb-1.5">Client PO reference (optional)</div>
                    <p className="text-[10px] text-slate-500 mb-2.5 leading-snug">
                        Defaults inherited from the AMC schedule. Override per-invoice if the offline bill was raised under a different client PO.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[11px] font-medium text-slate-600">Client PO Number</Label>
                            <Input
                                value={form.external_po_number}
                                onChange={(e) => set("external_po_number", e.target.value)}
                                placeholder="e.g. CLIENT/PO/2026/0042"
                                maxLength={80}
                                className="mt-1"
                                data-testid="offline-client-po-number"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] font-medium text-slate-600">Client PO Date</Label>
                            <Input
                                type="date"
                                value={form.external_po_date}
                                onChange={(e) => set("external_po_date", e.target.value)}
                                className="mt-1"
                                data-testid="offline-client-po-date"
                            />
                        </div>
                    </div>
                </div>

                {/* PDF / image attachment */}
                <div>
                    <Label className="text-xs font-semibold">Invoice copy (optional · PDF / PNG / JPG · ≤10 MB)</Label>
                    <div
                        ref={dropRef}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                        className="mt-1.5 border-2 border-dashed border-slate-200 rounded-md p-4 text-center text-sm hover:bg-slate-50 transition-colors"
                        data-testid="offline-attachment-drop"
                    >
                        {file ? (
                            <div className="flex items-center justify-center gap-2 text-slate-700">
                                <Paperclip size={14} />
                                <span className="font-medium" data-testid="offline-attachment-name">{file.name}</span>
                                <span className="text-xs text-slate-500">({(file.size / 1024).toFixed(0)} KB)</span>
                                <button
                                    type="button"
                                    onClick={() => setFile(null)}
                                    className="text-slate-400 hover:text-rose-600 ml-2"
                                    data-testid="offline-attachment-clear"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <label className="cursor-pointer text-slate-600 hover:text-slate-900">
                                <Upload size={20} className="mx-auto mb-1 text-slate-400" />
                                <div className="text-xs">
                                    Drag &amp; drop or <span className="text-slate-900 underline-offset-2 underline">browse</span>
                                </div>
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    className="hidden"
                                    data-testid="offline-attachment-input"
                                />
                            </label>
                        )}
                    </div>
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel disabled={submitting} testId="offline-cancel">Cancel</FormDialogCancel>
                <Button
                    onClick={submit}
                    disabled={submitting}
                    className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                    data-testid="offline-submit"
                >
                    <Receipt size={14} /> {submitting ? "Recording…" : "Record offline invoice"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
